/**
 * ONBOARDING VIEW - REDESIGNED
 * 
 * New flow:
 * 1. Core message display
 * 2. Main objective selection (ONE only)
 * 3. Promise selection (7/14/30 days)
 * 4. Ritual declaration (7-day ritual)
 * 5. Physical data
 * 6. Health & allergies
 * 7. Terms
 * 8. Plan selection
 */

import React, { useState } from 'react';
import { ShieldCheck, User as UserIcon, Activity, Target, Flame, ChevronRight, Rocket, Sparkles, Star, Zap, Lock } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { User, SupportedLanguage } from './types';
import { LOGO_URL } from './constants';
import { useTranslation } from 'react-i18next';
import { getCoreMessage, getDifferentiator } from './constants/coreMessaging';
import { setUserPromise } from './services/promiseService';
import { startRitual } from './services/ritualService';

export const OnboardingView = ({ user, lang, onComplete }: { user: User, lang: SupportedLanguage, onComplete: () => void }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [mainObjective, setMainObjective] = useState<'strength' | 'endurance' | 'weight_loss' | 'muscle_gain' | 'flexibility' | 'general_health' | null>(null);
  const [promiseDays, setPromiseDays] = useState<7 | 14 | 30 | null>(null);
  const [formData, setFormData] = useState({
    age: user.age || 0,
    weight: user.weight || 0,
    height: user.height || 0,
    gender: user.gender || 'male',
    allergies: user.allergies || '',
    hasAcceptedTerms: false
  });

  const handleFinalizeOnboarding = async () => {
    const userRef = doc(db, "users", user.id);
    
    // Set promise and start ritual
    if (promiseDays) {
      await setUserPromise(user.id, promiseDays);
    }
    await startRitual(user.id);
    
    await updateDoc(userRef, {
      ...formData,
      mainObjective: mainObjective || 'general_health',
      hasOnboarded: true,
      hasAcceptedTerms: formData.hasAcceptedTerms
    });
    onComplete();
  };

  const objectives = [
    { id: 'strength' as const, label: t('objective.strength'), icon: <Target /> },
    { id: 'endurance' as const, label: t('objective.endurance'), icon: <Zap /> },
    { id: 'weight_loss' as const, label: t('objective.weight_loss'), icon: <Activity /> },
    { id: 'muscle_gain' as const, label: t('objective.muscle_gain'), icon: <Star /> },
    { id: 'flexibility' as const, label: t('objective.flexibility'), icon: <Sparkles /> },
    { id: 'general_health' as const, label: t('objective.general_health'), icon: <UserIcon /> }
  ];

  const promiseOptions = [
    { days: 7 as const, label: t('core.promise_7') },
    { days: 14 as const, label: t('core.promise_14') },
    { days: 30 as const, label: t('core.promise_30') }
  ];

  return (
    <div className="fixed inset-0 z-[300] bg-slate-950 flex items-center justify-center p-4 overflow-y-auto">
      <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-[3rem] p-8 space-y-8 shadow-2xl relative overflow-hidden">
        
        <div className="mb-8 text-center">
            <img src={LOGO_URL} alt="Logo" className="w-32 h-32 inline-block mb-2 object-contain" />
            <h1 className="text-3xl font-light tracking-wider bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400 drop-shadow-lg">
                VitaCore
            </h1>
        </div>

        {/* Progress Dots */}
        {step < 7 && (
          <div className="flex justify-center gap-2">
            {[0, 1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className={`h-1.5 rounded-full transition-all ${step === i ? 'w-8 bg-emerald-500' : 'w-2 bg-slate-800'}`} />
            ))}
          </div>
        )}

        {/* STEP 0: Core Message */}
        {step === 0 && (
          <div className="space-y-6 animate-fadeIn text-center">
            <div className="space-y-4">
              <p className="text-lg font-bold text-emerald-400 italic leading-relaxed">
                "{getCoreMessage(lang)}"
              </p>
              <p className="text-sm text-slate-400 font-medium">
                {getDifferentiator(lang)}
              </p>
            </div>
            <button 
              onClick={() => setStep(1)} 
              className="w-full bg-emerald-500 py-4 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors"
            >
              {t('onboarding.btn_next')} <ChevronRight size={18}/>
            </button>
          </div>
        )}

        {/* STEP 1: Main Objective Selection */}
        {step === 1 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center">
              <h2 className="text-2xl font-black uppercase mb-2">{t('objective.title')}</h2>
              <p className="text-slate-500 text-sm">{t('objective.subtitle')}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {objectives.map(obj => (
                <button
                  key={obj.id}
                  onClick={() => setMainObjective(obj.id)}
                  className={`p-4 rounded-2xl border-2 transition-all ${
                    mainObjective === obj.id
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                      : 'border-slate-800 bg-slate-800/50 text-slate-400 hover:border-slate-700'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    {obj.icon}
                    <span className="text-xs font-black uppercase">{obj.label}</span>
                  </div>
                </button>
              ))}
            </div>
            <button 
              disabled={!mainObjective}
              onClick={() => setStep(2)} 
              className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed py-4 rounded-2xl font-black flex items-center justify-center gap-2 transition-all"
            >
              {t('onboarding.btn_next')} <ChevronRight size={18}/>
            </button>
          </div>
        )}

        {/* STEP 2: Promise Selection */}
        {step === 2 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center">
              <h2 className="text-2xl font-black uppercase mb-2">{t('promise.title')}</h2>
              <p className="text-slate-500 text-sm">{t('promise.select')}</p>
            </div>
            <div className="space-y-3">
              {promiseOptions.map(option => (
                <button
                  key={option.days}
                  onClick={() => setPromiseDays(option.days)}
                  className={`w-full p-6 rounded-2xl border-2 transition-all text-left ${
                    promiseDays === option.days
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                      : 'border-slate-800 bg-slate-800/50 text-slate-400 hover:border-slate-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-black text-lg uppercase">{option.label}</span>
                    {promiseDays === option.days && <Star className="text-emerald-500" size={20} fill="currentColor" />}
                  </div>
                </button>
              ))}
            </div>
            <button 
              disabled={!promiseDays}
              onClick={() => setStep(3)} 
              className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed py-4 rounded-2xl font-black flex items-center justify-center gap-2 transition-all"
            >
              {t('onboarding.btn_next')} <ChevronRight size={18}/>
            </button>
          </div>
        )}

        {/* STEP 3: Ritual Declaration */}
        {step === 3 && (
          <div className="space-y-6 animate-fadeIn text-center">
            <div className="w-20 h-20 bg-orange-500/10 text-orange-500 rounded-3xl flex items-center justify-center mx-auto mb-4">
              <Flame size={40} fill="currentColor" />
            </div>
            <div className="space-y-4">
              <h2 className="text-3xl font-black uppercase">{t('ritual.title')}</h2>
              <p className="text-lg font-bold text-emerald-400 italic leading-relaxed">
                "{t('ritual.subtitle')}"
              </p>
              <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 space-y-3">
                <div className="flex items-center justify-center gap-2 text-emerald-400">
                  <Lock size={20} />
                  <span className="font-black uppercase text-sm">{t('ritual.unlocks')}</span>
                </div>
                <p className="text-sm text-slate-300">{t('ritual.mission_day_1')}</p>
                <p className="text-xs text-slate-500">{t('ritual.reward_preview')}</p>
              </div>
            </div>
            <button 
              onClick={() => setStep(4)} 
              className="w-full bg-emerald-500 py-4 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors"
            >
              {t('onboarding.btn_next')} <ChevronRight size={18}/>
            </button>
          </div>
        )}

        {/* STEP 4: Physical Data */}
        {step === 4 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-500/10 text-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <UserIcon size={32} />
              </div>
              <h2 className="text-2xl font-black uppercase">{t('onboarding.title_1')}</h2>
              <p className="text-slate-500 text-sm">{t('onboarding.sub_1')}</p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-2">{t('onboarding.age')}</label>
                  <input 
                    type="number" 
                    value={formData.age || ''} 
                    onChange={e => setFormData({...formData, age: parseInt(e.target.value)})}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl p-4 outline-none focus:border-emerald-500 text-white"
                    placeholder="25"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-2">{t('onboarding.weight')}</label>
                  <input 
                    type="number" 
                    value={formData.weight || ''} 
                    onChange={e => setFormData({...formData, weight: parseInt(e.target.value)})}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl p-4 outline-none focus:border-emerald-500 text-white"
                    placeholder="70"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-2">{t('onboarding.height')}</label>
                <input 
                  type="number" 
                  value={formData.height || ''} 
                  onChange={e => setFormData({...formData, height: parseInt(e.target.value)})}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl p-4 outline-none focus:border-emerald-500 text-white"
                  placeholder="175"
                />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-2">{t('onboarding.gender')}</label>
                <div className="flex bg-slate-800 border border-slate-700 p-1 rounded-xl">
                  <button 
                    onClick={() => setFormData({...formData, gender: 'male'})}
                    className={`flex-1 py-3 text-xs font-black rounded-lg transition-all ${formData.gender === 'male' ? 'bg-emerald-500 text-white shadow-lg' : 'text-slate-500'}`}
                  >
                    {t('onboarding.gender_male')}
                  </button>
                  <button 
                    onClick={() => setFormData({...formData, gender: 'female'})}
                    className={`flex-1 py-3 text-xs font-black rounded-lg transition-all ${formData.gender === 'female' ? 'bg-emerald-500 text-white shadow-lg' : 'text-slate-500'}`}
                  >
                    {t('onboarding.gender_female')}
                  </button>
                </div>
              </div>
            </div>
            <button onClick={() => setStep(5)} className="w-full bg-emerald-500 py-4 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors">
              {t('onboarding.btn_next')} <ChevronRight size={18}/>
            </button>
          </div>
        )}

        {/* STEP 5: Health & Allergies */}
        {step === 5 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/10 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Activity size={32} />
              </div>
              <h2 className="text-2xl font-black uppercase">{t('onboarding.title_2')}</h2>
              <p className="text-slate-500 text-sm">{t('onboarding.sub_2')}</p>
            </div>
            <textarea 
              value={formData.allergies}
              onChange={e => setFormData({...formData, allergies: e.target.value})}
              placeholder={t('onboarding.allergies_placeholder')}
              className="w-full h-32 bg-slate-800 border border-slate-700 rounded-xl p-4 outline-none focus:border-emerald-500 text-white resize-none"
            />
            <button onClick={() => setStep(6)} className="w-full bg-emerald-500 py-4 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors">
              {t('onboarding.btn_next')} <ChevronRight size={18}/>
            </button>
          </div>
        )}

        {/* STEP 6: Terms */}
        {step === 6 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-500/10 text-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <ShieldCheck size={32} />
              </div>
              <h2 className="text-2xl font-black uppercase">{t('onboarding.title_3')}</h2>
              <p className="text-slate-500 text-sm">{t('onboarding.sub_3')}</p>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-xl text-[10px] text-slate-400 leading-relaxed max-h-40 overflow-y-auto border border-slate-700">
              {lang === SupportedLanguage.IT ? (
                `L'utilizzo dell'app VitaCore implica l'accettazione che i consigli forniti dall'AI Coach sono a scopo informativo. 
                Consultare sempre un medico prima di iniziare nuovi regimi alimentari o di allenamento. 
                VitaCore non è responsabile per infortuni o reazioni allergiche derivanti dall'uso dei piani generati.
                Accettando, confermi di aver compreso che VitaCore è uno strumento di supporto e non sostituisce il parere medico professionale.`
              ) : (
                `Using the VitaCore app implies acceptance that advice provided by the AI Coach is for informational purposes only.
                Always consult a physician before starting new diet or workout regimens.
                VitaCore is not responsible for injuries or allergic reactions resulting from the use of generated plans.
                By accepting, you confirm understanding that VitaCore is a support tool and does not replace professional medical advice.`
              )}
            </div>
            <label className="flex items-center gap-3 p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-xl cursor-pointer">
              <input 
                type="checkbox" 
                checked={formData.hasAcceptedTerms}
                onChange={e => setFormData({...formData, hasAcceptedTerms: e.target.checked})}
                className="w-5 h-5 accent-emerald-500 rounded-md" 
              />
              <span className="text-xs font-bold text-slate-200">{t('onboarding.terms_accept')}</span>
            </label>
            <button 
              disabled={!formData.hasAcceptedTerms}
              onClick={() => setStep(7)} 
              className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 py-4 rounded-2xl font-black flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-500/20"
            >
              {t('onboarding.btn_next')} <ChevronRight size={18}/>
            </button>
          </div>
        )}

        {/* STEP 7: Plan Selection */}
        {step === 7 && (
          <div className="space-y-8 animate-scaleIn text-center py-4">
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-500/20 blur-[60px] rounded-full" />
              <div className="relative z-10 w-24 h-24 bg-gradient-to-tr from-emerald-500 to-cyan-400 rounded-3xl flex items-center justify-center mx-auto mb-6 rotate-12 shadow-2xl">
                <Rocket size={48} className="text-white animate-bounce" fill="currentColor" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-4xl font-black italic tracking-tighter text-white uppercase">{t('onboarding.title_4')}</h2>
              <p className="text-slate-400 text-sm font-medium">{t('onboarding.sub_4')}</p>
            </div>

            <div className="grid grid-cols-3 gap-2 px-4 opacity-50">
              {[<Star key="1"/>, <Sparkles key="2"/>, <Zap key="3"/>].map((icon, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div className="text-emerald-500">{icon}</div>
                  <div className="h-1 w-full bg-slate-800 rounded-full" />
                </div>
              ))}
            </div>

            <div className="space-y-4">
              <button 
                onClick={handleFinalizeOnboarding}
                className="group relative w-full bg-white text-black py-6 rounded-[2rem] font-black text-xl tracking-tighter hover:bg-emerald-500 hover:text-white transition-all shadow-[0_20px_50px_rgba(16,185,129,0.3)] flex items-center justify-center gap-3 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                {t('onboarding.btn_finalize')} <ChevronRight size={24} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em]">{t('onboarding.final_config')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
